import React, {useState} from 'react';
import {styled} from '@mui/system';
import {Box, Typography, Grid} from '@mui/material';
import PoweredByAmbitionBox from '../PoweredByAB';
import {getNestedProperty} from '@jobseeker/lotus/utils';
import {isArray} from 'lodash';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

const companyHeadingEnum = {
  FOUNDED_YEAR: 'FOUNDED_YEAR',
  TOTAL_EMPLOYEES_INDIA: 'TOTAL_EMPLOYEES_INDIA',
  TOTAL_EMPLOYEES: 'TOTAL_EMPLOYEES',
  HEADQUARTERS: 'HEADQUARTERS',
  OFFICE_LOCATIONS: 'OFFICE_LOCATIONS',
  PRIMARY_INDUSTRY: 'PRIMARY_INDUSTRY',
  OTHER_INDUSTRY: 'OTHER_INDUSTRY',
  NATURE_OF_BUSINESS: 'NATURE_OF_BUSINESS',
} as const;

interface CompanyInfo {
  [key: string]: string | number | any[]; // Adjusted for flexibility
}

interface CompanyInfoMap {
  key: string;
  value: string;
  keyName: string;
}

const companyInfoMap: Record<string, CompanyInfoMap> = {
  FOUNDED_YEAR: {
    key: 'Founding Year',
    value: 'foundedYear',
    keyName: 'FOUNDED_YEAR',
  },
  TOTAL_EMPLOYEES_INDIA: {
    key: 'Employees Count (India)',
    value: 'totalEmployeesIndia',
    keyName: 'TOTAL_EMPLOYEES_INDIA',
  },
  TOTAL_EMPLOYEES: {
    key: 'Employees Count (Global)',
    value: 'totalEmployees',
    keyName: 'TOTAL_EMPLOYEES',
  },
  HEADQUARTERS: {
    key: 'Headquarter Location',
    value: 'hq',
    keyName: 'HEADQUARTERS',
  },
  OFFICE_LOCATIONS: {
    key: 'Other Office Locations',
    value: 'officeLocations',
    keyName: 'OFFICE_LOCATIONS',
  },
  PRIMARY_INDUSTRY: {
    key: 'Primary Industry',
    value: 'primaryIndustry',
    keyName: 'PRIMARY_INDUSTRY',
  },
  OTHER_INDUSTRY: {
    key: 'Other Industries',
    value: 'otherTags',
    keyName: 'OTHER_INDUSTRY',
  },
  NATURE_OF_BUSINESS: {
    key: 'Nature of Business',
    value: 'natureOfBusiness',
    keyName: 'NATURE_OF_BUSINESS',
  },
};

const createMarkup = (title: string) => ({__html: title});

const CompanyInfoWrapper = styled(Box)(({theme}) => ({
  borderRadius: 12,
  boxShadow: '0px 6.48px 6px 2px rgba(41, 41, 41, 0.02);',
  backgroundColor: '#fff',
  marginTop: isAppIimjobs ? 16 : 0,
  padding: '24px 16px 15px 16px',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('md')]: {
    borderTopLeftRadius: isAppIimjobs ? 0 : 12,
    borderTopRightRadius: isAppIimjobs ? 0 : 12,
    marginTop: 0,
    paddingTop: 16,
  },
}));

const CompanyDesc = styled('span', {
  shouldForwardProp: prop => prop !== 'theme',
})(({theme}) => ({
  fontSize: isAppIimjobs ? 14 : 16,
  fontWeight: isAppIimjobs ? 500 : 400,
  lineHeight: 1.5,
  color: isAppIimjobs ? '#333' : '#000',
  '& a': {
    color: '#006ed7',
  },
  display: 'inline', // Ensures the text stays on the same line
  [theme.breakpoints.down('sm')]: {
    fontSize: isAppIimjobs ? 12 : 16,
  },
}));
const CompanyOtherWrapper = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
}));

const CompanyRow = styled(Grid)(({theme}) => ({
  marginTop: 16,
  marginBottom: 10,
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gridTemplateRows: 'repeat(2, auto)',
  gap: isAppIimjobs ? '18px' : '30px',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridTemplateRows: 'repeat(4, auto)',
    gap: isAppIimjobs ? 12 : 30,
    alignItems: 'flex-start',
    width: '100%',
    columnGap: '0px',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: isAppIimjobs ? 'repeat(2, 1fr)' : 'repeat(1, 1fr)',
    gridTemplateRows: 'repeat(4, auto)',
    alignItems: 'flex-start',
    width: '100%',
    columnGap: '0px',
  },
}));

const CompanyCell = styled(Box)(({theme}) => ({
  marginRight: 15,
  maxWidth: 210,
}));

const CompanyCellHeading = styled(Typography)(({theme}) => ({
  fontSize: 14,
  lineHeight: 'normal',
  marginBottom: 1,
  color: isAppIimjobs ? '#7E7E7E' : '#212529',
  fontWeight: 500,
  [theme.breakpoints.down('md')]: {
    fontSize: isAppIimjobs ? 12 : 14,
  },
  opacity: isAppIimjobs ? 1 : 0.7,
}));

const CompanyCellValue = styled(Typography)(({theme}) => ({
  fontSize: isAppIimjobs ? 14 : 15,
  fontWeight: isAppIimjobs ? 500 : 'bold',
  lineHeight: 'normal',
  [theme.breakpoints.down('md')]: {
    fontSize: isAppIimjobs ? 12 : 15,
  },
}));

const ExpandSpan = styled('span')(({theme}) => ({
  cursor: 'pointer',
  color: isAppIimjobs ? ' #49966E' : '#000000',
  fontFamily: 'Poppins',
  fontSize: isAppIimjobs ? 14 : 15,
  fontStyle: 'normal',
  fontWeight: isAppIimjobs ? 500 : 'bold',
  textDecoration: isAppIimjobs ? 'none' : 'underline',
  lineHeight: 'normal',
  [theme.breakpoints.down('md')]: {
    fontSize: isAppIimjobs ? 12 : 15,
  },
}));

const DescReadMoreSpan = styled(Typography)(({theme}) => ({
  fontSize: 14,
  fontWeight: isAppIimjobs ? 500 : 600,
  color: isAppIimjobs ? ' #49966E' : '#e96309',
  cursor: 'pointer',
  display: 'inline',
  [theme.breakpoints.down('md')]: {
    fontSize: isAppIimjobs ? 12 : 14,
  },
}));

interface CellValue {
  value: string;
  clickableStr: string;
}

const getCellValue = (
  id: string,
  val: any,
  clickStr = '',
  clicked = false
): CellValue => {
  if (id === companyHeadingEnum.NATURE_OF_BUSINESS) {
    return {
      value: `(${val.join('/')})`,
      clickableStr: '',
    };
  }
  if (id === companyHeadingEnum.OTHER_INDUSTRY) {
    return {
      value: clicked ? val.join(', ') : `${val.slice(0, 2).join(', ')}`,
      clickableStr: clicked
        ? ''
        : val.length > 2
          ? `${isAppIimjobs ? '+' : ' & '}${val.length - 2} more`
          : '',
    };
  }
  if (id === companyHeadingEnum.OFFICE_LOCATIONS) {
    return {
      value: clicked
        ? val.map((e: any) => e.cityName).join(', ')
        : `${val
            .map((e: any) => e.cityName)
            .slice(0, 2)
            .join(', ')}`,
      clickableStr: clicked
        ? ''
        : val.length > 2
          ? `${isAppIimjobs ? '+' : ' & '}${val.length - 2} more`
          : '',
    };
  }
  return {value: val, clickableStr: clickStr};
};

interface GetCompanyCellComponentProps {
  companyInfo: CompanyInfo;
  section: CompanyInfoMap;
}

export const GetCompanyCellComponent: React.FC<
  GetCompanyCellComponentProps
> = ({companyInfo, section}) => {
  const heading = section.key;
  const value = companyInfo[section.value];
  if (!value || (isArray(value) && value.length === 0)) return null;

  const obj = getCellValue(section.keyName, value);
  const [displayValue, setDisplayValue] = useState<CellValue>(obj);
  const [displayClickableStr, setDisplayClickableStr] = useState<string>(
    obj.clickableStr
  );

  return (
    <CompanyCell>
      <CompanyCellHeading>{heading}</CompanyCellHeading>
      <CompanyCellValue>
        {displayValue.value}
        <ExpandSpan
          onClick={() => {
            setDisplayValue(getCellValue(section.keyName, value, '', true));
            setDisplayClickableStr('');
          }}
          data-testid="company-basic-info-expand-span"
        >
          {displayClickableStr}
        </ExpandSpan>
      </CompanyCellValue>
    </CompanyCell>
  );
};

interface CompanyBasicInfoProps {
  companyInfo: CompanyInfo;
}

const CompanyBasicInfo: React.FC<CompanyBasicInfoProps> = ({companyInfo}) => {
  if (!companyInfo) return null;

  const [ABDesc, setABDesc] = useState<string>(
    `${(getNestedProperty(companyInfo, 'aboutCompany') || '')
      .substr(0, 300)
      .trim()} `
  );
  const [showABDescReadMore, setShowABDescReadMore] = useState<boolean>(
    String(companyInfo.aboutCompany || '').trim().length > 300
  );
  const [showABDescReadLess, setShowABDescReadLess] = useState<boolean>(false);

  const finalCompanyInfoArray: CompanyInfoMap[] = [];
  Object.keys(companyInfoMap).forEach(key => {
    if (
      companyInfo[companyInfoMap[key]?.value] &&
      (!isArray(companyInfo[companyInfoMap[key]?.value]) ||
        String(companyInfo[companyInfoMap[key]?.value]).length > 0)
    ) {
      finalCompanyInfoArray.push(companyInfoMap[key]);
    }
  });

  if (ABDesc.length === 1 && finalCompanyInfoArray.length === 0) return null;

  return (
    <CompanyInfoWrapper>
      <CompanyDesc data-testid="company-basic-info-desc">
        <div
          dangerouslySetInnerHTML={{
            __html: ABDesc,
          }}
        ></div>
        {showABDescReadMore && !showABDescReadLess && (
          <DescReadMoreSpan
            onClick={() => {
              setShowABDescReadMore(false);
              setABDesc(getNestedProperty(companyInfo, 'aboutCompany') || '');
              setShowABDescReadLess(true);
            }}
          >
            Read More
          </DescReadMoreSpan>
        )}

        {showABDescReadLess && !showABDescReadMore && (
          <DescReadMoreSpan
            onClick={() => {
              setShowABDescReadMore(true);
              setABDesc(
                `${(getNestedProperty(companyInfo, 'aboutCompany') || '')
                  .substr(0, 300)
                  .trim()} `
              );
              setShowABDescReadLess(false);
            }}
          >
            &nbsp;Read Less
          </DescReadMoreSpan>
        )}
      </CompanyDesc>
      <CompanyOtherWrapper>
        <CompanyRow>
          {finalCompanyInfoArray.map(section => (
            <GetCompanyCellComponent
              key={section.key}
              companyInfo={companyInfo}
              section={section}
            />
          ))}
        </CompanyRow>
      </CompanyOtherWrapper>
    </CompanyInfoWrapper>
  );
};

export default CompanyBasicInfo;
